import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-black/25 backdrop-blur-sm'
      }`}
    >
      <div className="flex items-center justify-between h-16 px-4 md:px-10">
        {/* Left: Nav Links (Desktop) */}
        <div className="hidden md:flex items-center gap-8">
          <Link
            to="/"
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              scrolled || !isHome ? 'text-foreground-800 hover:text-foreground-950' : 'text-white hover:text-white/80'
            }`}
          >
            Home
          </Link>
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                scrolled || !isHome ? 'text-foreground-800 hover:text-foreground-950' : 'text-white hover:text-white/80'
              }`}
            >
              Categories
              <i
                className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                  dropdownOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setDropdownOpen(false)}
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/allchinabuy-sheet-guide"
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              scrolled || !isHome ? 'text-foreground-800 hover:text-foreground-950' : 'text-white hover:text-white/80'
            }`}
          >
            Shopping Guide
          </Link>
          <Link
            to="/allchinabuy-sheet-safety"
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              scrolled || !isHome ? 'text-foreground-800 hover:text-foreground-950' : 'text-white hover:text-white/80'
            }`}
          >
            Safety
          </Link>
        </div>

        {/* Mobile: Hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer rounded-full transition-colors ${
            scrolled || !isHome ? 'text-foreground-800 hover:bg-background-100' : 'text-white hover:bg-white/10'
          }`}
          aria-label="Menu"
        >
          <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>

        {/* Center: Logo */}
        <div className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              scrolled || !isHome ? 'bg-primary-500' : 'bg-white/20'
            }`}
          >
            <i className={`ri-file-list-3-line text-sm ${scrolled || !isHome ? 'text-background-50' : 'text-white'}`}></i>
          </div>
          <span
            className={`text-lg font-heading font-semibold whitespace-nowrap hidden sm:inline ${
              scrolled || !isHome ? 'text-foreground-950' : 'text-white'
            }`}
          >
            AllChinaBuySpreadsheet
          </span>
        </div>

        {/* Right: Icons + Buttons (Desktop) */}
        <div className="hidden md:flex items-center gap-3">
          <button
            className={`w-9 h-9 flex items-center justify-center cursor-pointer rounded-full transition-colors ${
              scrolled || !isHome ? 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10'
            }`}
            aria-label="Search"
          >
            <i className="ri-search-line text-lg"></i>
          </button>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-2 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            allchinabuy sheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-2 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            buy it now
          </a>
        </div>

        {/* Mobile: buy it now button */}
        <div className="md:hidden">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-3 py-1.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            buy now
          </a>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
            >
              Home
            </Link>
            <button
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
            >
              Categories
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${mobileDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileDropdownOpen && (
              <div className="pl-4 space-y-1 border-l-2 border-background-200 ml-3">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => { setMobileDropdownOpen(false); setMobileMenuOpen(false); }}
                    className="block px-3 py-2 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
            <Link
              to="/allchinabuy-sheet-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
            >
              Shopping Guide
            </Link>
            <Link
              to="/allchinabuy-sheet-safety"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
            >
              Safety
            </Link>
            <div className="pt-3 border-t border-background-200 mt-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-center px-4 py-2.5 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors cursor-pointer"
              >
                allchinabuy sheet
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}